// components/MultiPropertyPreviewPage.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Mail,
    Loader2,
    AlertCircle,
    Building2,
    Pencil,
    CheckCircle,
    X,
    Globe,
    IdCard
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import MultiTemplate, { MultiTemplateData } from "@/components/templates/MultiTemplate";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import phoneIcon from "@/assets/telephone_black.png";
import mailIcon from "@/assets/mail_white.png";
import "@/styles/template-responsive.css";

interface Property {
    id: string;
    title: string;
    image: string;
    photos?: string[];
    price: number;
    address: string;
    bedrooms: number;
    bathrooms: number;
    area: number;
    city?: string;
    state?: string;
    status?: string;
    description?: string;
    subject?: string;
    is_open_house?: boolean;
    agentName?: string;
    agentPhone?: string;
    agentEmail?: string;
    agentAvatar?: string;
    companyLogo?: string;
    company?: string;
    license_number?: string;
    website?: string;
    [key: string]: any;
}

interface UserProfile {
    id?: string;
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    company?: string;
    company_logo?: string;
    website?: string;
    avatar?: string;
    profile_image?: string;
    license_number?: string;
}

interface EditedPropertyData extends Partial<Property> {
    imageFile?: File;
    secondaryImageFile?: File;
}

interface MultiPropertyPreviewPageProps {
    properties: Property[];
    getFullUrl: (url: string | null | undefined) => string;
    selectedTemplateCount?: number;
    onBack: () => void;
    onContinue: () => void;
}

const MultiPropertyPreviewPage: React.FC<MultiPropertyPreviewPageProps> = ({
    properties: initialProperties,
    getFullUrl,
    selectedTemplateCount = 0,
    onBack,
    onContinue,
}) => {
    const { toast } = useToast();

    const [properties, setProperties] = useState<Property[]>(initialProperties);
    const [emailSubject, setEmailSubject] = useState("");
    const [sendingProof, setSendingProof] = useState(false);
    const [proofSentCount, setProofSentCount] = useState<number | null>(null);
    const [isApproved, setIsApproved] = useState(false);
    const [editingPropertyIndex, setEditingPropertyIndex] = useState<number | null>(null);
    const [editedData, setEditedData] = useState<EditedPropertyData>({});
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingImagePropertyId, setUploadingImagePropertyId] = useState<string | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(true);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                setLoadingProfile(true);
                const response = await axiosInstance.get(endpoints.me);
                if (response.data) {
                    setUserProfile({
                        id: response.data.id,
                        email: response.data.email,
                        phone: response.data.phone || response.data.phone_number || response.data.mobile,
                        first_name: response.data.first_name,
                        last_name: response.data.last_name,
                        full_name: response.data.full_name || `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim(),
                        company: response.data.company || response.data.company_name,
                        company_logo: response.data.company_logo || response.data.companyLogo,
                        website: response.data.website || response.data.company_website,
                        avatar: response.data.avatar || response.data.profile_image,
                        license_number: response.data.license_number,
                    });
                }
            } catch (error) {
                console.error("Failed to fetch user profile:", error);
                setUserProfile({ email: "contact@example.com", phone: "", website: "" });
            } finally {
                setLoadingProfile(false);
            }
        };
        fetchUserProfile();
    }, []);

    useEffect(() => { setProperties(initialProperties); }, [initialProperties]);

    const companyLogoUrl = userProfile?.company_logo
        ? getFullUrl(userProfile.company_logo)
        : mainlogo;

    useEffect(() => {
        try { sessionStorage.setItem("multiPropertyEmailSubject", emailSubject); } catch (e) { }
    }, [emailSubject]);

    useEffect(() => {
        try {
            const saved = sessionStorage.getItem("multiPropertyEmailSubject");
            if (saved) setEmailSubject(saved);
        } catch (e) { }
    }, []);

    const handleStartEdit = (index: number) => { setEditingPropertyIndex(index); setEditedData({}); };
    const handleCancelEdit = () => { setEditingPropertyIndex(null); setEditedData({}); };

    const handlePropertyChange = useCallback((fields: Partial<MultiTemplateData>) => {
        setEditedData(prev => {
            const updates: Partial<Property> = { ...prev };
            if (fields.price !== undefined) updates.price = fields.price;
            if (fields.bedrooms !== undefined) updates.bedrooms = fields.bedrooms;
            if (fields.bathrooms !== undefined) updates.bathrooms = fields.bathrooms;
            if (fields.area !== undefined) updates.area = fields.area;
            if (fields.address !== undefined) updates.address = fields.address;
            if (fields.city !== undefined) updates.city = fields.city;
            if (fields.state !== undefined) updates.state = fields.state;
            if (fields.status !== undefined) updates.status = fields.status;
            if (fields.description !== undefined) updates.description = fields.description;
            if (fields.title !== undefined) updates.title = fields.title;
            if (fields.image !== undefined) updates.image = fields.image;
            if (fields.agentName !== undefined) updates.agentName = fields.agentName;
            if (fields.agentPhone !== undefined) updates.agentPhone = fields.agentPhone;
            if (fields.agentEmail !== undefined) updates.agentEmail = fields.agentEmail;
            if (fields.website !== undefined) updates.website = fields.website;
            return updates;
        });
    }, []);

    const handleImageChange = useCallback((propertyId: string, file: File, type: 'main' | 'secondary') => {
        setEditedData(prev => {
            const updated: EditedPropertyData = { ...prev };
            if (type === 'main') {
                updated.imageFile = file;
            } else {
                updated.secondaryImageFile = file;
            }
            return updated;
        });
    }, []);

    const handleSaveEdit = async () => {
        if (editingPropertyIndex === null) return;
        const property = properties[editingPropertyIndex];
        if (!property) return;
        setIsSaving(true);
        try {
            const updatedProperty = { ...property, ...editedData };
            let websiteUrl = updatedProperty.website || '';
            if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
                websiteUrl = 'https://' + websiteUrl;
            }

            const hasImageFile = editedData.imageFile instanceof File;
            const hasSecondaryImageFile = editedData.secondaryImageFile instanceof File;

            if (hasImageFile || hasSecondaryImageFile) {
                const formData = new FormData();
                formData.append('title', updatedProperty.title || '');
                formData.append('price', String(updatedProperty.price || ''));
                if (updatedProperty.bedrooms !== undefined) formData.append('bedrooms', String(updatedProperty.bedrooms));
                if (updatedProperty.bathrooms !== undefined) formData.append('bathrooms', String(updatedProperty.bathrooms));
                if (updatedProperty.area !== undefined) formData.append('area', String(updatedProperty.area));
                if (updatedProperty.address !== undefined) formData.append('address', updatedProperty.address || '');
                if (updatedProperty.city !== undefined) formData.append('city', updatedProperty.city || '');
                if (updatedProperty.state !== undefined) formData.append('state', updatedProperty.state || '');
                if (updatedProperty.status !== undefined) formData.append('status', updatedProperty.status || '');
                if (updatedProperty.description !== undefined) formData.append('description', updatedProperty.description || '');
                if (websiteUrl) formData.append('website', websiteUrl);
                if (hasImageFile && editedData.imageFile) {
                    formData.append('image', editedData.imageFile);
                    // A freshly-picked main photo becomes its own untouched
                    // "original" too, so Edit Image (MultiTemplate.tsx) always
                    // re-crops from this new photo instead of a stale original
                    // left over from before this replacement.
                    formData.append('original_image', editedData.imageFile);
                }
                if (hasSecondaryImageFile && editedData.secondaryImageFile) formData.append('photos', editedData.secondaryImageFile);

                await axiosInstance.patch(
                    `${endpoints.properties}${property.id}/`,
                    formData,
                    { headers: { "Content-Type": undefined as any }, transformRequest: [(data) => data] }
                );
            } else {
                await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
                    title: updatedProperty.title, price: updatedProperty.price,
                    bedrooms: updatedProperty.bedrooms, bathrooms: updatedProperty.bathrooms,
                    area: updatedProperty.area, address: updatedProperty.address,
                    city: updatedProperty.city, state: updatedProperty.state,
                    status: updatedProperty.status, description: updatedProperty.description, website: websiteUrl,
                });
            }

            const fullResponse = await axiosInstance.get(`${endpoints.properties}${property.id}/`);
            setProperties(prev => prev.map((p, idx) => {
                if (idx === editingPropertyIndex) {
                    return {
                        ...p, ...fullResponse.data,
                        image: getFullUrl(fullResponse.data.image || fullResponse.data.hero_image_url),
                        photos: fullResponse.data.photos ? fullResponse.data.photos.map((url: string) => getFullUrl(url)) : p.photos,
                        website: fullResponse.data.website || '',
                    };
                }
                return p;
            }));
            toast({ title: "Changes Saved", description: "Property updated successfully.", duration: 1000 });
            setEditingPropertyIndex(null);
            setEditedData({});
        } catch (error) {
            toast({ title: "Save Failed", description: "Could not save changes. Please try again.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendProof = async () => {
        if (editingPropertyIndex !== null) {
            toast({ title: "Save Changes First", description: "Please save or cancel your edits before sending proof.", variant: "destructive", duration: 1000 });
            return;
        }
        if (!emailSubject.trim()) {
            toast({ title: "Subject Required", description: "Please enter an email subject before sending proof.", variant: "destructive", duration: 1000 });
            return;
        }
        if (properties.length === 0) {
            toast({ title: "No Properties", description: "Please select at least one property to send proof.", variant: "destructive", duration: 1000 });
            return;
        }
        setSendingProof(true);
        try {
            const meRes = await axiosInstance.get(endpoints.me);
            const userEmail = meRes.data?.email;
            if (!userEmail) {
                toast({ title: "No Email Found", description: "Could not retrieve your email address.", variant: "destructive", duration: 1000 });
                setSendingProof(false);
                return;
            }
            await axiosInstance.post(endpoints.email_proof || "campaign/email/send-proof/", {
                campaign_type: "multi_property",
                template_path: "multiproperty/template.html",
                subject: emailSubject,
                user_email: userEmail,
                template_count: properties.length,
                properties: properties.map((prop) => ({
                    id: prop.id, title: prop.title, address: prop.address,
                    city: prop.city || "", state: prop.state || "",
                    price: prop.price, bedrooms: prop.bedrooms, bathrooms: prop.bathrooms,
                    area: prop.area, description: prop.description || "",
                    status: prop.status || "", image: prop.image, photos: prop.photos || [],
                })),
            });
            setProofSentCount((prev) => (prev || 0) + 1);
            setIsApproved(false);
            toast({ title: "Proof Sent!", description: `Preview email sent to ${userEmail} with ${properties.length} properties.`, duration: 1000 });
        } catch (error: any) {
            toast({ title: "Failed to send proof", description: error?.response?.data?.error || "Please try again.", variant: "destructive" });
        } finally {
            setSendingProof(false);
        }
    };

    const handleContinue = () => {
        if (!emailSubject.trim()) {
            toast({ title: "Subject Required", description: "Please enter an email subject before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        if (!proofSentCount) {
            toast({ title: "Proof Required", description: "Please send and review the proof email before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        if (!isApproved) {
            toast({ title: "Approval Required", description: "Please confirm you have reviewed and approved the proof.", variant: "destructive", duration: 1000 });
            return;
        }
        if (editingPropertyIndex !== null) {
            toast({ title: "Save Changes", description: "Please save or cancel your edits before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        try {
            sessionStorage.setItem("multiSelectedProperties", JSON.stringify(properties));
            sessionStorage.setItem("multiPropertyEmailSubject", emailSubject);
        } catch (e) { }
        onContinue();
    };

    const getPropertyWithEdits = (index: number): Property => {
        const property = properties[index];
        if (index === editingPropertyIndex) return { ...property, ...editedData };
        return property;
    };

    const formatWebsiteDisplay = (website: string | undefined): string => {
        if (!website) return "";
        return website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
    };

    const getWebsiteUrl = (website: string | undefined): string => {
        if (!website) return "#";
        if (website.startsWith("http://") || website.startsWith("https://")) return website;
        return `https://${website}`;
    };

    return (
        <div className="template-preview-page template-bottom-safe w-full max-w-[1400px] mx-auto">
            {/* ---- Page Title ---- */}
            <div className="text-center mb-6 sm:mb-8 px-2">
                <h2 className="page-title text-primary">
                    Preview Multi-Property Launch
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base">
                    Review your {properties.length} {properties.length === 1 ? "property" : "properties"} before sending
                </p>
            </div>

            {/* ---- Email Subject Card ---- */}
            <Card className="mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    {/* Subject row — stacks on mobile, side-by-side on sm+ */}
                    <div className="subject-row">
                        <div className="w-full sm:flex-1 sm:max-w-xl">
                            <h3 className="section-title mb-2">Email Subject</h3>
                            <Input
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="Enter email subject"
                                className="text-base sm:text-lg"
                            />
                        </div>
                        <Badge variant="secondary" className="text-sm sm:text-lg px-3 sm:px-4 py-1.5 sm:py-2 self-start sm:self-auto mt-9">
                            {properties.length} {properties.length === 1 ? "Property" : "Properties"}
                        </Badge>
                    </div>
                </CardContent>
            </Card>

            {/* ---- Read-only Preview Notice ---- */}
            {editingPropertyIndex === null && (
                <div className="template-preview-banner mb-4 sm:mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="text-blue-800 flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>This is a read-only preview. Click <strong>"Edit Template"</strong> to make changes.</span>
                    </p>
                </div>
            )}

            {/* ================================================================
                EMAIL PREVIEW CONTAINER
            ================================================================ */}
            <div className="mb-4 sm:mb-6 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
                <Card className="template-preview-frame inline-block min-w-[760px] w-fit max-w-none align-top">

                    {/* ---- EMAIL HEADER ---- */}
                    <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}

                    >
                        {loadingProfile ? (
                            <div className="flex justify-center">
                                <Loader2 className="h-6 w-6 text-white animate-spin" />
                            </div>
                        ) : (
                            /* Flexbox replaces nested tables for better responsive behaviour */
                            <div className="flex items-center justify-between gap-4 sm:gap-6  p-2">

                                {/* Left: avatar + name + contact info */}
                                <div className="flex min-w-0 items-center gap-3 sm:gap-4" >
                                    {userProfile?.avatar ? (
                                        <img
                                            src={getFullUrl(userProfile.avatar)}
                                            alt={userProfile.full_name || "User"}
                                            className="block h-[120px] w-[90px] rounded-xl object-cover shadow-sm flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="flex h-[90px] w-[90px] rounded-xl flex-shrink-0 items-center justify-center border-2 border-white/40 bg-white/15 shadow-sm">
                                            <span className="text-lg font-semibold text-white">
                                                {(userProfile?.first_name?.[0] || userProfile?.email?.[0] || 'U').toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="truncate text-base font-semibold leading-tight text-white sm:text-lg">
                                            {userProfile?.full_name || `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Real Estate Agent'}
                                        </p>
                                        <div className="flex flex-col gap-1 text-sm leading-5 text-white/90">
                                            {userProfile?.phone && (
                                                <span className="inline-flex items-center gap-2 truncate">
                                                    <img src={phoneIcon} alt="Phone" className="h-3.5 w-3.5 flex-shrink-0 brightness-0 invert" />
                                                    <span className="truncate">{userProfile.phone}</span>
                                                </span>
                                            )}
                                            {userProfile?.email && (
                                                <span className="inline-flex items-center gap-2 truncate">
                                                    <img src={mailIcon} alt="Email" className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate underline">
                                                        {/* {userProfile.email} */}
                                                     Click here to email Me
                                                    </span>
                                                </span>
                                            )}
                                            {userProfile?.company && (
                                                <span className="inline-flex items-center gap-2 truncate text-blue-100">
                                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate">{userProfile.company}</span>
                                                </span>
                                            )}
                                            {userProfile?.license_number && (
                                                <span className="inline-flex items-center gap-2 truncate text-blue-100">
                                                    <IdCard className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate">License #: {userProfile.license_number}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: company logo rectangle */}
                                <div className="flex items-center justify-center flex-shrink-0">
                                    <div className="h-[120px] w-[120px] overflow-hidden rounded-xl shadow-sm flex items-center justify-center">
                                        <img src={companyLogoUrl} alt="Company Logo" className="block h-full w-full rounded-xl object-contain" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ---- CONTENT AREA ---- */}
                    <CardContent className="template-preview-shell bg-white p-0">
                        <div className="space-y-6 sm:space-y-8 py-4 sm:py-4">
                            {properties.map((property, index) => {
                                const isEditing = editingPropertyIndex === index;
                                const displayProperty = getPropertyWithEdits(index);

                                return (
                                    <div key={property.id} className="relative border-b border-gray-200 px-0 pb-4 last:border-b-0">
                                        {/* Per-property toolbar */}
                                        <div className="template-preview-toolbar mb-3 mx-6">
                                            <h4 className="font-medium text-gray-700 flex items-center gap-2">
                                                {isEditing && (
                                                    <Badge className="bg-amber-500 text-white text-xs">Editing</Badge>
                                                )}
                                            </h4>

                                            {isEditing ? (
                                                <div className="template-preview-actions">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleCancelEdit}
                                                        disabled={isSaving}
                                                        className="flex-1 sm:flex-none"
                                                    >
                                                        <X className="h-4 w-4 mr-1" />
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={handleSaveEdit}
                                                        disabled={isSaving}
                                                        className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                                                    >
                                                        {isSaving ? (
                                                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</>
                                                        ) : (
                                                            <><CheckCircle className="h-4 w-4 mr-1" />Save Changes</>
                                                        )}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleStartEdit(index)}
                                                    disabled={editingPropertyIndex !== null && editingPropertyIndex !== index}
                                                    className="w-full sm:w-auto mx-2"
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" />
                                                    Edit Template
                                                </Button>
                                            )}
                                        </div>

                                        {/* Edit mode banner */}
                                        {isEditing && (
                                            <div className="template-preview-banner mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 m-6">
                                                <p className="text-amber-800 flex items-start gap-2">
                                                    <Pencil className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                                    <span><strong>Edit Mode:</strong> Click on any field to edit it directly. Don't forget to save your changes!</span>
                                                </p>
                                            </div>
                                        )}

                                        {/* Template slot */}
                                        <div className={`template-slot-wrapper template-slot-wrapper--property ${!isEditing && editingPropertyIndex !== null ? 'template-slot-dimmed' : ''}`}>
                                            <MultiTemplate
                                                index={index + 1}
                                                property={{
                                                    ...displayProperty,
                                                    license_number: userProfile?.license_number,
                                                }}
                                                getFullUrl={getFullUrl}
                                                readOnly={!isEditing}
                                                onChange={isEditing ? handlePropertyChange : undefined}
                                                onImageChange={isEditing ? (file, type) => handleImageChange(property.id, file, type) : undefined}
                                                onViewListing={() => window.open(`/properties/${property.id}`, '_blank')}
                                                onContact={() => toast({ title: "Contact", description: `Contact for ${property.address}`, duration: 1000 })}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>

                    {/* ---- EMAIL FOOTER ---- */}
                    <div
                        style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}
                        className="px-4 py-5 sm:px-10 sm:py-7"
                    >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4">
                            {/* Logo + copyright */}
                            <div className="flex items-center gap-3 sm:gap-4">
                                <a href="https://mylistiq.com" target="_blank" rel="noopener noreferrer">
                                    <div className="w-14 h-14 sm:w-[70px] sm:h-[70px] rounded-2xl bg-white flex items-center justify-center flex-shrink-0">
                                        <img src={mainlogo} alt="MyListIQ" className="w-11 h-11 sm:w-[60px] sm:h-[60px] object-contain" />
                                    </div>
                                </a>
                                <span className="text-white/85 text-xs sm:text-sm leading-5">
                                    © {new Date().getFullYear()} MyListIQ.<br className="sm:hidden" /> All rights reserved.
                                </span>
                            </div>

                            {/* Property count pill */}
                            <span
                                className="self-start sm:self-auto text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-full"
                                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                            >
                                {properties.length} {properties.length === 1 ? 'Property' : 'Properties'} Available
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
            {/* ================================================================
                END EMAIL PREVIEW CONTAINER
            ================================================================ */}

            {/* ---- Send Proof Card ---- */}
            <Card className="template-preview-card border-blue-200 bg-blue-50/50 mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    <div className="template-proof-row flex gap-3 sm:gap-4">
                        <div className="template-proof-icon w-10 h-10 bg-blue-100 rounded-full items-center justify-center flex-shrink-0">
                            <Mail className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="section-title text-blue-900 mb-1">Send Proof Email</h3>
                            <p className="text-xs sm:text-sm text-blue-800 mb-3 sm:mb-4">
                                Preview how your multi-property email will look before sending.
                            </p>
                            <Button
                                size="sm"
                                onClick={handleSendProof}
                                disabled={sendingProof || editingPropertyIndex !== null}
                                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                            >
                                {sendingProof ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                                ) : proofSentCount && proofSentCount > 0 ? (
                                    <><Mail className="mr-2 h-4 w-4" />Resend Proof</>
                                ) : (
                                    <><Mail className="mr-2 h-4 w-4" />Send Proof</>
                                )}
                            </Button>

                            {editingPropertyIndex !== null && (
                                <p className="text-xs sm:text-sm text-amber-600 mt-2">
                                    Please save or cancel your edits before sending proof.
                                </p>
                            )}

                            {proofSentCount && proofSentCount > 0 && editingPropertyIndex === null && (
                                <div className="mt-3 flex items-center gap-2 text-xs sm:text-sm text-green-700">
                                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                                    <span>Proof email sent. Check your inbox.</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ---- Approval Card ---- */}
            <Card className="template-preview-card border-amber-300 bg-amber-50/70 mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    <label className="flex items-start gap-3 sm:gap-4 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={isApproved}
                            onChange={(e) => setIsApproved(e.target.checked)}
                            disabled={!proofSentCount || proofSentCount === 0 || editingPropertyIndex !== null}
                            className="mt-1 w-5 h-5 rounded text-amber-600 flex-shrink-0"
                        />
                        <div className="min-w-0">
                            <p className="section-title text-amber-900 mb-1">
                                I have reviewed and approve this as final
                            </p>
                            <p className="text-xs sm:text-sm text-amber-800">
                                By checking this box, you confirm that all {properties.length}{" "}
                                {properties.length === 1 ? "property is" : "properties are"} correct and ready to be sent.
                            </p>
                        </div>
                    </label>
                </CardContent>
            </Card>

            {/* ---- Navigation Footer ---- */}
            <div className="template-footer-actions">
                <Button
                    size="lg"
                    variant="outline"
                    onClick={onBack}
                    disabled={editingPropertyIndex !== null || isSaving}
                    className="w-full sm:w-auto"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Back
                </Button>

                <Button
                    size="lg"
                    disabled={!(proofSentCount && proofSentCount > 0 && isApproved) || editingPropertyIndex !== null}
                    onClick={handleContinue}
                    className={`w-full sm:w-auto bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600
                        ${!(proofSentCount && proofSentCount > 0 && isApproved) || editingPropertyIndex !== null
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:from-blue-500 hover:via-blue-600 hover:to-blue-700"
                        }
                    `}
                >
                    Looks Good – Continue
                    <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
            </div>
        </div>
    );
};

export default MultiPropertyPreviewPage;
